import { type ErrorKind } from '@/analytics';
import { pageForRoute, type PageRoute } from '@/site/pageRegistry';
import type { DatelineLocale } from '@/components/marketing';
import { SITE_HEADER_EN, type SiteHeaderContent } from '@/components/marketing/SiteHeader';

export interface HeroContent {
    titleLine1: string;
    titleLine2: string;
    subtitle: string;
    dropPrompt: string;
    or: string;
    chooseFile: string;
    loading: string;
    startMarkup: string;
    differentFile: string;
    chipNothingUploaded: string;
    /** Free (D1). */
    chipFree: string;
    /**
     * The one offline claim (docs/offline.md; src/site/offlineClaims.test.ts allows only this wording): the service
     * worker precaches the local viewer on the first visit, and e2e/offline.spec.ts proves it.
     */
    chipOffline: string;
    chipNoAccount: string;
    unitImperial: string;
    unitMetric: string;
    /** The accessible name of the units picker. */
    unitLabel: string;
    invalidPdfMessage: string;
    unreadablePdfMessage: string;
    errors: Record<ErrorKind, string>;
}

export interface ToolContent {
    name: string;
    desc: string;
}

export interface ToolShowcaseContent {
    sectionTitle: string;
    sectionLead: string;
    carouselAriaLabel: string;
    /** Alt text of a tool screenshot; `{tool}` is replaced by the tool's name. */
    shotAlt: string;
    tools: readonly ToolContent[];
}

export interface FeatureGridContent {
    sectionTitle: string;
    sectionLead: string;
    cards: ReadonlyArray<{ title: string; body: string; accent?: true }>;
}

export interface BimDataContent {
    sectionTitle: string;
    sectionLead: string;
    items: ReadonlyArray<{ title: string; body: string }>;
    learnMore: string;
}

/** The site top bar's words (components/marketing/SiteHeader); the landing pages carry them per locale. */
export type HeaderContent = SiteHeaderContent;

/** The landing footer's words. `navLabels` overrides the registry (English) label per page link. */
export interface FooterContent {
    navLabels: Readonly<Partial<Record<Exclude<PageRoute, '/'>, string>>>;
    relatedTools: string;
    comparePdfs: string;
    flattenPdf: string;
    fillAndSign: string;
}

export interface LandingPageContent {
    /** The dateline's language. */
    locale: DatelineLocale;
    header: HeaderContent;
    footer: FooterContent;
    hero: HeroContent;
    toolShowcase: ToolShowcaseContent;
    featureGrid: FeatureGridContent;
    bimData: BimDataContent;
}

export const LANDING_HREFLANG_ALTERNATES: ReadonlyArray<{ hreflang: string; href: string }> = [
    { hreflang: 'en', href: pageForRoute('/').canonical },
    { hreflang: 'de', href: pageForRoute('/de').canonical },
    { hreflang: 'fr', href: pageForRoute('/fr').canonical },
    { hreflang: 'x-default', href: pageForRoute('/').canonical },
];

export const MEASURE_HREFLANG_ALTERNATES: ReadonlyArray<{ hreflang: string; href: string }> = [
    { hreflang: 'en', href: pageForRoute('/measure-pdf-online').canonical },
    { hreflang: 'de', href: pageForRoute('/de/pdf-messen').canonical },
    { hreflang: 'fr', href: pageForRoute('/fr/mesurer-pdf').canonical },
    { hreflang: 'x-default', href: pageForRoute('/measure-pdf-online').canonical },
];

export const EN_CONTENT: LandingPageContent = {
    locale: 'en',
    header: SITE_HEADER_EN,
    footer: {
        navLabels: {},
        relatedTools: 'Related tools:',
        comparePdfs: 'Compare PDFs',
        flattenPdf: 'Flatten PDF',
        fillAndSign: 'PDF Fill & Sign',
    },
    hero: {
        titleLine1: 'Open a PDF.',
        titleLine2: 'Start marking it up.',
        subtitle: 'PDF markup software for construction drawing review — redline, dimension, and annotate any PDF right in your browser, without uploading it. Use it as a PDF markup program on Mac, Windows, or Linux: no install, no account.',
        dropPrompt: 'Drop your PDF here',
        or: 'or',
        chooseFile: 'Choose file',
        loading: 'Loading...',
        startMarkup: 'Start markup',
        differentFile: 'Different file',
        chipNothingUploaded: 'Nothing uploaded',
        chipFree: 'Free',
        chipOffline: 'Works offline after your first visit',
        chipNoAccount: 'No account needed',
        unitImperial: 'Imperial (feet, inches)',
        unitMetric: 'Metric (meters, cm)',
        unitLabel: 'Units',
        invalidPdfMessage: 'That file is not a PDF. Choose a PDF file and try again.',
        unreadablePdfMessage: 'Could not read that file. Choose a PDF file and try again.',
        errors: {
            'out-of-memory': 'This PDF is too large to open in this browser tab. Close other tabs or try a smaller file.',
            'chunk-load': 'Part of the app failed to load. Refresh the page and try again.',
            'pdf-parse': 'This PDF could not be opened. Try a different PDF file.',
            'pdf-encrypted': 'This PDF is password protected. Remove the password and try again.',
            network: 'The app could not finish loading. Check your connection and try again.',
            aborted: 'Opening the PDF was cancelled. Try again.',
            unknown: 'Could not open that PDF. Try again with a different PDF file.',
        },
    },
    toolShowcase: {
        sectionTitle: 'Every markup and annotation tool, free in your browser',
        sectionLead: 'Redline, dimension, measure, and annotate — no install and no account. Hover to explore, or just watch them go by.',
        carouselAriaLabel: 'Markup tools',
        shotAlt: '{tool} tool used on a drawing',
        tools: [
            { name: 'Dimension', desc: 'Place architectural dimensions that read the right scale per viewport.' },
            { name: 'Line', desc: 'Straight lines and leaders, with snapping.' },
            { name: 'Polyline', desc: 'Chain segments into an open path.' },
            { name: 'Polygon', desc: 'Close an area for area + perimeter takeoffs.' },
            { name: 'Rectangle', desc: 'Box regions and details.' },
            { name: 'Ellipse', desc: 'Circles and ovals.' },
            { name: 'Revision cloud', desc: 'Cloud around changes for review.' },
            { name: 'Arrow', desc: 'Point to what matters.' },
            { name: 'Freehand', desc: 'Sketch freehand ink.' },
            { name: 'Highlight', desc: 'Highlight text and regions.' },
            { name: 'Callout', desc: 'Text boxes with leaders.' },
            { name: 'Text', desc: 'Drop notes anywhere on the sheet.' },
            { name: 'Measure to scale', desc: 'Length, area, and perimeter takeoffs calibrated to your drawing.' },
            { name: 'bim overlay', desc: 'Open a bim pdf to see model data on the drawing.' },
            { name: 'Export', desc: 'Flatten to a stamped PDF, or export markups to CSV.' },
        ],
    },
    featureGrid: {
        sectionTitle: 'Local by default. No account needed.',
        sectionLead: 'Everything runs on your own machine — open, mark up, and export without signing in.',
        cards: [
            {
                title: 'Opens on your device',
                body: 'Drop in a PDF and it loads straight into your browser — nothing is uploaded. Private and instant.',
            },
            {
                title: 'The full toolset, free',
                body: 'Redline, measure to scale, overlay bim data, and export a finished PDF. The whole kit, no signup, no seat to buy.',
            },
            {
                title: 'Yours to keep',
                body: 'Every export is a standard PDF or CSV — no proprietary format, no lock-in, no subscription.',
                accent: true,
            },
        ],
    },
    bimData: {
        sectionTitle: 'See your BIM, right on the PDF.',
        sectionLead: 'Open a bim pdf and every sheet carries its model data with it — no separate viewer, no round trip to authoring software.',
        items: [
            { title: 'Element-level data', body: 'Click any object on the drawing to see its parameters, properties, and model IDs — pulled straight from the source model.' },
            { title: 'Never drifts from the model', body: 'A bim pdf carries model data inside the PDF itself, so field markups and bim data stay tied to the same drawing.' },
            { title: 'Open, published schema', body: 'bim pdf is a versioned, publicly documented format — any tool can read or generate one, not just ours.' },
        ],
        learnMore: 'Learn about the bim pdf format →',
    },
};

export const DE_CONTENT: LandingPageContent = {
    locale: 'de',
    header: {
        navLabel: 'Website',
        howItWorks: 'So funktioniert es',
        guides: 'Anleitungen',
        whatsNew: 'Neuigkeiten',
        openPdf: 'PDF öffnen — kostenlos',
        signIn: 'Anmelden',
        menu: 'Menü',
        languageLabel: 'Sprache',
        installApp: 'App installieren',
        installHint: 'Zum Home-Bildschirm hinzufügen, damit es offline bleibt',
    },
    footer: {
        navLabels: {
            '/de/pdf-messen': 'PDF messen',
            '/ipad-pdf-markup': 'PDF-Markierungen auf dem iPad',
            '/bluebeam-for-mac': 'Bluebeam für Mac',
            '/bluebeam-revu-free-alternative': 'Kostenlose Alternative zu Bluebeam Revu',
            '/bluebeam-revu-20-end-of-life': 'Bluebeam Revu 20: Ende des Lebenszyklus',
            '/measure-pdf-online': 'PDF online messen',
            '/drawing-scales': 'Zeichnungsmaßstäbe',
            '/redline-pdf-drawing': 'PDF-Zeichnung kommentieren',
            '/measure-area-pdf': 'PDF-Bereich messen',
            '/calibrate-pdf-scale': 'PDF-Massstab kalibrieren',
            '/share-pdf-drawings-for-review': 'PDF-Zeichnungen zur Pruefung teilen',
            '/free-pdf-markup-tools': 'Kostenlose PDF-Markup-Tools im Vergleich',
            '/whats-new': 'Neuigkeiten',
            '/de': 'Deutsch',
            '/fr': 'Français',
        },
        relatedTools: 'Weitere Tools:',
        comparePdfs: 'PDFs vergleichen',
        flattenPdf: 'PDF verflachen',
        fillAndSign: 'PDF ausfüllen und unterschreiben',
    },
    hero: {
        titleLine1: 'Eine PDF öffnen.',
        titleLine2: 'Direkt loslegen.',
        subtitle: 'PDF-Markup-Software für die Bauzeichnungs-Prüfung — Rotabzeichnungen, Bemaßungen und Anmerkungen direkt im Browser, ohne Upload. Als PDF-Markup-Programm auf Mac, Windows und Linux nutzbar: kein Install, kein Konto.',
        dropPrompt: 'PDF hier ablegen',
        or: 'oder',
        chooseFile: 'Datei wählen',
        loading: 'Laden...',
        startMarkup: 'Markup starten',
        differentFile: 'Andere Datei',
        chipNothingUploaded: 'Kein Upload',
        chipFree: 'Kostenlos',
        chipOffline: 'Funktioniert nach dem ersten Besuch offline',
        chipNoAccount: 'Kein Konto nötig',
        unitImperial: 'Imperial (Fuß, Zoll)',
        unitMetric: 'Metrisch (Meter, cm)',
        unitLabel: 'Einheiten',
        invalidPdfMessage: 'Diese Datei ist keine PDF. Bitte eine PDF-Datei wählen und erneut versuchen.',
        unreadablePdfMessage: 'Diese Datei konnte nicht gelesen werden. Bitte eine PDF-Datei wählen.',
        errors: {
            'out-of-memory': 'Diese PDF ist zu groß für diesen Browser-Tab. Andere Tabs schließen oder eine kleinere Datei versuchen.',
            'chunk-load': 'Ein Teil der App konnte nicht geladen werden. Seite neu laden und erneut versuchen.',
            'pdf-parse': 'Diese PDF konnte nicht geöffnet werden. Bitte eine andere PDF-Datei versuchen.',
            'pdf-encrypted': 'Diese PDF ist passwortgeschützt. Passwort entfernen und erneut versuchen.',
            network: 'Die App konnte nicht vollständig geladen werden. Verbindung prüfen und erneut versuchen.',
            aborted: 'Das Öffnen der PDF wurde abgebrochen. Erneut versuchen.',
            unknown: 'Diese PDF konnte nicht geöffnet werden. Mit einer anderen PDF-Datei erneut versuchen.',
        },
    },
    toolShowcase: {
        sectionTitle: 'Jedes Markup- und Annotationswerkzeug, kostenlos im Browser',
        sectionLead: 'Rotabzeichnungen, Bemaßungen, Maße und Anmerkungen — kein Install, kein Konto. Erkunden oder einfach zusehen.',
        carouselAriaLabel: 'Markup-Werkzeuge',
        shotAlt: 'Werkzeug „{tool}“ auf einer Zeichnung',
        tools: [
            { name: 'Bemaßung', desc: 'Architektonische Bemaßungen platzieren, die den richtigen Maßstab je Ansicht lesen.' },
            { name: 'Linie', desc: 'Gerade Linien und Führungslinien, mit Einrasten.' },
            { name: 'Polylinie', desc: 'Segmente zu einem offenen Pfad verbinden.' },
            { name: 'Polygon', desc: 'Fläche für Flächen- und Umfangsberechnungen schließen.' },
            { name: 'Rechteck', desc: 'Bereiche und Details einrahmen.' },
            { name: 'Ellipse', desc: 'Kreise und Ovale.' },
            { name: 'Revisionscloud', desc: 'Cloud um Änderungen für die Überprüfung.' },
            { name: 'Pfeil', desc: 'Auf das Wesentliche zeigen.' },
            { name: 'Freihand', desc: 'Freihandlinien skizzieren.' },
            { name: 'Markierung', desc: 'Text und Bereiche hervorheben.' },
            { name: 'Sprechblase', desc: 'Textfelder mit Führungslinien.' },
            { name: 'Text', desc: 'Notizen überall auf der Seite platzieren.' },
            { name: 'Maß halten', desc: 'Längen-, Flächen- und Umfangsberechnungen kalibriert auf Ihre Zeichnung.' },
            { name: 'BIM-Überlagerung', desc: 'Eine bim pdf öffnen, um Modelldaten auf der Zeichnung zu sehen.' },
            { name: 'Export', desc: 'Zu einer gestempelten PDF zusammenführen oder Markierungen als CSV exportieren.' },
        ],
    },
    featureGrid: {
        sectionTitle: 'Lokal als Standard. Kein Konto erforderlich.',
        sectionLead: 'Alles läuft auf Ihrem eigenen Gerät — öffnen, kommentieren und exportieren, ohne sich anzumelden.',
        cards: [
            {
                title: 'Öffnet auf Ihrem Gerät',
                body: 'PDF ablegen und es wird direkt im Browser geladen — nichts wird hochgeladen. Privat und sofort.',
            },
            {
                title: 'Das volle Werkzeugset, kostenlos',
                body: 'Rotabzeichnungen, Bemaßungen, BIM-Daten einblenden und fertiges PDF exportieren. Das komplette Paket, kein Anmelden, kein Kauf.',
            },
            {
                title: 'Gehört Ihnen',
                body: 'Jeder Export ist eine Standard-PDF oder CSV — kein proprietäres Format, kein Lock-in, kein Abonnement.',
                accent: true,
            },
        ],
    },
    bimData: {
        sectionTitle: 'BIM-Daten direkt auf der PDF sehen.',
        sectionLead: 'Eine bim pdf öffnen und jede Seite enthält ihre Modelldaten — kein separater Viewer, kein Umweg über die Autorensoftware.',
        items: [
            { title: 'Daten auf Elementebene', body: 'Ein beliebiges Objekt auf der Zeichnung anklicken, um seine Parameter, Eigenschaften und Modell-IDs zu sehen — direkt aus dem Quellmodell.' },
            { title: 'Immer synchron mit dem Modell', body: 'Eine bim pdf trägt die Modelldaten in der PDF selbst, sodass Feldmarkierungen und BIM-Daten an dieselbe Zeichnung gebunden bleiben.' },
            { title: 'Offenes, dokumentiertes Schema', body: 'bim pdf ist ein versioniertes, öffentlich dokumentiertes Format — jedes Werkzeug kann es lesen oder erzeugen, nicht nur unseres.' },
        ],
        learnMore: 'Mehr über das bim-pdf-Format erfahren →',
    },
};

export const FR_CONTENT: LandingPageContent = {
    locale: 'fr',
    header: {
        navLabel: 'Site',
        howItWorks: 'Comment ça marche',
        guides: 'Guides',
        whatsNew: 'Nouveautés',
        openPdf: 'Ouvrir un PDF — gratuit',
        signIn: 'Se connecter',
        menu: 'Menu',
        languageLabel: 'Langue',
        installApp: "Installer l'app",
        installHint: "Ajoutez-le à l'écran d'accueil pour le garder hors ligne",
    },
    footer: {
        navLabels: {
            '/fr/mesurer-pdf': 'Mesurer un PDF',
            '/ipad-pdf-markup': 'Annoter un PDF sur iPad',
            '/bluebeam-for-mac': 'Bluebeam pour Mac',
            '/bluebeam-revu-free-alternative': 'Alternative gratuite à Bluebeam Revu',
            '/bluebeam-revu-20-end-of-life': 'Fin de vie de Bluebeam Revu 20',
            '/measure-pdf-online': 'Mesurer un PDF en ligne',
            '/drawing-scales': 'Échelles de dessin',
            '/redline-pdf-drawing': 'Annoter un plan PDF',
            '/measure-area-pdf': 'Mesurer la surface du PDF',
            '/calibrate-pdf-scale': 'Calibrer l\'échelle du PDF',
            '/share-pdf-drawings-for-review': 'Partager des plans PDF pour revision',
            '/free-pdf-markup-tools': 'Outils d’annotation PDF gratuits comparés',
            '/whats-new': 'Nouveautés',
            '/de': 'Deutsch',
            '/fr': 'Français',
        },
        relatedTools: 'Outils associés :',
        comparePdfs: 'Comparer des PDF',
        flattenPdf: 'Aplatir un PDF',
        fillAndSign: 'Remplir et signer un PDF',
    },
    hero: {
        titleLine1: 'Ouvrir un PDF.',
        titleLine2: 'Commencer les annotations.',
        subtitle: 'Logiciel de balisage PDF pour la révision de plans de construction — dessinez, mesurez et annotez n’importe quel PDF directement dans votre navigateur, sans le télécharger. Utilisez-le comme logiciel de balisage PDF sur Mac, Windows ou Linux : aucune installation, aucun compte.',
        dropPrompt: 'Déposez votre PDF ici',
        or: 'ou',
        chooseFile: 'Choisir un fichier',
        loading: 'Chargement...',
        startMarkup: 'Démarrer le balisage',
        differentFile: 'Autre fichier',
        chipNothingUploaded: 'Aucun upload',
        chipFree: 'Gratuit',
        chipOffline: 'Fonctionne hors ligne après votre première visite',
        chipNoAccount: 'Aucun compte requis',
        unitImperial: 'Impérial (pieds, pouces)',
        unitMetric: 'Métrique (mètres, cm)',
        unitLabel: 'Unités',
        invalidPdfMessage: "Ce fichier n’est pas un PDF. Veuillez choisir un fichier PDF et réessayer.",
        unreadablePdfMessage: "Ce fichier n’a pas pu être lu. Veuillez choisir un fichier PDF.",
        errors: {
            'out-of-memory': "Ce PDF est trop volumineux pour cet onglet. Fermez d’autres onglets ou essayez un fichier plus petit.",
            'chunk-load': "Une partie de l’application n’a pas pu se charger. Rechargez la page et réessayez.",
            'pdf-parse': "Ce PDF n’a pas pu être ouvert. Essayez un autre fichier PDF.",
            'pdf-encrypted': "Ce PDF est protégé par un mot de passe. Supprimez le mot de passe et réessayez.",
            network: "L’application n’a pas pu finir de se charger. Vérifiez votre connexion et réessayez.",
            aborted: "L’ouverture du PDF a été annulée. Réessayez.",
            unknown: "Impossible d’ouvrir ce PDF. Réessayez avec un autre fichier PDF.",
        },
    },
    toolShowcase: {
        sectionTitle: 'Tous les outils de balisage et d’annotation, gratuits dans votre navigateur',
        sectionLead: 'Redlines, dimensions, mesures et annotations — sans installation et sans compte. Survolez pour explorer ou regardez défiler.',
        carouselAriaLabel: 'Outils de balisage',
        shotAlt: 'Outil « {tool} » utilisé sur un plan',
        tools: [
            { name: 'Cote', desc: 'Placez des cotes architecturales qui lisent la bonne échelle par vue.' },
            { name: 'Ligne', desc: 'Lignes droites et lignes de repère, avec accrochage.' },
            { name: 'Polyligne', desc: 'Chaînez des segments en un chemin ouvert.' },
            { name: 'Polygone', desc: 'Fermez une zone pour les métrés de surface et périmètre.' },
            { name: 'Rectangle', desc: 'Encadrez des zones et des détails.' },
            { name: 'Ellipse', desc: 'Cercles et ovales.' },
            { name: 'Nuage de révision', desc: 'Nuage autour des modifications pour la révision.' },
            { name: 'Flèche', desc: 'Pointez ce qui est important.' },
            { name: 'Main levée', desc: 'Esquissez en encre libre.' },
            { name: 'Surlignage', desc: 'Surlignez du texte et des zones.' },
            { name: 'Bulle', desc: 'Zones de texte avec lignes de repère.' },
            { name: 'Texte', desc: "Déposez des notes n’importe où sur la feuille." },
            { name: 'Mesure à l’échelle', desc: "Métrés de longueur, surface et périmètre calibrés sur votre dessin." },
            { name: 'Superposition BIM', desc: 'Ouvrez un bim pdf pour voir les données de maquette sur le dessin.' },
            { name: 'Export', desc: 'Aplatissez en PDF tamponné ou exportez les annotations en CSV.' },
        ],
    },
    featureGrid: {
        sectionTitle: 'Local par défaut. Aucun compte nécessaire.',
        sectionLead: 'Tout fonctionne sur votre propre machine — ouvrez, annotez et exportez sans vous connecter.',
        cards: [
            {
                title: "S’ouvre sur votre appareil",
                body: "Déposez un PDF et il se charge directement dans votre navigateur — rien n’est envoyé vers un serveur. Privé et instantané.",
            },
            {
                title: 'La boîte à outils complète, gratuite',
                body: "Redlines, mesures à l’échelle, superposition BIM et export PDF. Tout le kit, sans inscription ni abonnement.",
            },
            {
                title: 'À vous pour toujours',
                body: 'Chaque export est un PDF standard ou un CSV — aucun format propriétaire, aucun verrouillage, aucun abonnement.',
                accent: true,
            },
        ],
    },
    bimData: {
        sectionTitle: 'Visualisez vos données BIM directement sur le PDF.',
        sectionLead: "Ouvrez un bim pdf et chaque feuille porte ses données de maquette — aucun visionneur séparé, aucun aller-retour vers le logiciel auteur.",
        items: [
            { title: "Données au niveau de l’élément", body: "Cliquez sur n’importe quel objet du dessin pour voir ses paramètres, propriétés et identifiants de maquette — extraits directement du modèle source." },
            { title: 'Toujours synchronisé avec la maquette', body: 'Un bim pdf intègre les données de maquette dans le PDF lui-même, de sorte que les annotations terrain et les données BIM restent liées au même plan.' },
            { title: 'Schéma ouvert et documenté', body: "bim pdf est un format versionné et documenté publiquement — n’importe quel outil peut le lire ou en générer un, pas seulement le nôtre." },
        ],
        learnMore: 'En savoir plus sur le format bim pdf →',
    },
};
